import React from 'react';
import {FormProvider, useFieldArray, useForm, useFormContext} from 'react-hook-form';
import type {ProyectoFormDTO} from '../../../../api/Objetos';
import {participacionApi} from '../../../../api/api';
import {createEmptyProyecto} from '../emptyProyecto';
import {FieldGrid, NumberField, TextField, ToggleField} from '../../../../ui/form/fields';
import {
    EmptyState,
    FormSection,
    ItemCard,
    buildCardTitle,
    buildSummary,
    hasPersistedId,
    useProjectReadOnly,
    BulkToggle
} from '../projectForm.helpers';
import {SectionAlerts, SectionSaveButton, useCollectionSection} from '../sectionCrudUtils';
import {useRegisterSectionSave} from '../SectionSaveCoordinator';
import BarraCargando from "../../../../ui/BarraCargando";
import { useTranslation } from 'react-i18next';

export function ParticipacionSection(props: { proyectoId: number; showIds?: boolean }) {
    const readOnly = useProjectReadOnly();
    const form = useForm<ProyectoFormDTO>({defaultValues: createEmptyProyecto()});
    const section = useCollectionSection({
        queryKey: ['admin', 'proyectos', 'participacion'],
        proyectoId: props.proyectoId,
        getFn: participacionApi.list,
        createFn: participacionApi.create,
        updateFn: participacionApi.update,
        deleteFn: participacionApi.delete,
    });

    React.useEffect(() => {
        form.reset({...createEmptyProyecto(), participacionCiudadana: section.data ?? []});
    }, [form, section.data]);

    const handleSave = form.handleSubmit(async (value) => {
        await section.save(value.participacionCiudadana ?? []);
    });

    useRegisterSectionSave({
        id: 'participacion',
        isDirty: form.formState.isDirty,
        isSaving: section.saveMutation.isPending,
        save: async () => {
            await handleSave();
        },
    });

    return (
        <FormProvider {...form}>
            <ParticipacionSectionFields
                showIds={Boolean(props.showIds)}
                isLoading={section.isLoading}
                loadError={section.error}
            />
            {!readOnly && (
                <>
                    <SectionSaveButton onClick={() => void handleSave()} busy={section.saveMutation.isPending}/>
                </>
            )}
        </FormProvider>
    );
}

function ParticipacionSectionFields(props: { showIds: boolean, isLoading: boolean, loadError: any }) {
    const { t } = useTranslation();
    const form = useFormContext<ProyectoFormDTO>();
    const participaciones = useFieldArray({control: form.control, name: 'participacionCiudadana'});
    const showId = (path: string) => props.showIds && hasPersistedId(form.watch(path as never));
    const readOnly = useProjectReadOnly();

    const setAllPublic = (isPublic: boolean) => {
        participaciones.fields.forEach((field, index) => {
            form.setValue(`participacionCiudadana.${index}.publico`, isPublic, { shouldDirty: true });
        });
    };

    const currentParticipaciones = form.watch('participacionCiudadana') || [];

    return (
        <FormSection
            id="participacion"
            title={t('participacionSection.title', { count: participaciones.fields.length })}
            collapsible
            defaultOpen={false}
            headerActions={!readOnly &&
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                    {props.isLoading && <BarraCargando size="1.2em" />}
                    <BulkToggle
                        label={t('participacionSection.public')}
                        fields={currentParticipaciones}
                        onToggle={setAllPublic}
                    />
                </div>
            }
        >
            <SectionAlerts loadError={props.loadError}/>
            {participaciones.fields.length === 0 && <EmptyState message={t('participacionSection.noParticipations')}/>}
            {participaciones.fields.map((field, index) => (
                <ItemCard
                    key={field.id}
                    title={buildCardTitle(t, t('participacionSection.participation'), index, form.watch(`participacionCiudadana.${index}.enlaceTexto`))}
                    summary={buildSummary(t, form.watch(`participacionCiudadana.${index}.enlaceTexto`))}
                    onRemove={() => participaciones.remove(index)}
                    defaultOpen={!hasPersistedId(form.watch(`participacionCiudadana.${index}.id` as never))}
                    actions={!readOnly &&
                        <ToggleField
                            label={t('participacionSection.public')}
                            name={`participacionCiudadana.${index}.publico`}
                            register={form.register}
                        />
                    }
                >
                    <FieldGrid>
                        {showId(`participacionCiudadana.${index}.id`) ?
                            <NumberField label={t('participacionSection.id')} name={`participacionCiudadana.${index}.id`}
                                         register={form.register}/> : null}
                        <TextField label={t('participacionSection.linkText')} name={`participacionCiudadana.${index}.enlaceTexto`}
                                   register={form.register}/>
                    </FieldGrid>
                </ItemCard>
            ))}

            {!readOnly ? (
                <button className="btn btnSecondary" type="button"
                        onClick={() => participaciones.append({id: 0, enlaceTexto: '', publico: false})}>
                    {t('participacionSection.addParticipation')}
                </button>
            ) : null}
        </FormSection>
    );
}
